import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, TrendingUp, Shield } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import VittaDerivativesPathways from '../components/VittaDerivativesPathways';

const DerivativesExam = () => {
  const derivativesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Derivatives Exam Prep Course - Options, Futures & Swaps",
    "description": "Comprehensive preparation for the CIRO Derivatives Exam. Master options, futures, forwards, swaps, hedging strategies, and derivatives regulation.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "url": "https://www.vittafinance.ca"
    },
    "courseCode": "DERIV-PREP",
    "educationalLevel": "Professional",
    "about": ["Derivatives Exam", "Options Trading", "Futures Trading", "Hedging Strategies"],
    "teaches": ["Options strategies", "Futures contracts", "Hedging techniques", "Pricing models", "Risk management"],
    "numberOfCredits": "120 questions in 3 hours",
    "offers": {
      "@type": "Offer",
      "price": "29.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    }
  };

  const derivativesFaqData = [
    {
      question: "What is the Derivatives exam?",
      answer: "The CIRO Derivatives Exam is a proficiency requirement for investment professionals who want to advise on or trade derivative products including options, futures, forwards, and swaps in Canada."
    },
    {
      question: "How many questions are on the Derivatives exam?",
      answer: "The Derivatives exam has 120 multiple-choice questions that must be completed in 3 hours. The passing score is determined by CIRO for each exam sitting."
    },
    {
      question: "What topics are covered on the Derivatives exam?",
      answer: "The exam covers options fundamentals, futures contracts, trading strategies (covered calls, protective puts, spreads, straddles), risk management, pricing models (Black-Scholes, Greeks), and regulatory requirements."
    },
    {
      question: "Do I need math skills for the Derivatives exam?",
      answer: "Yes, the Derivatives exam includes calculations for option pricing, Greeks (Delta, Gamma, Theta, Vega), margin requirements, and profit/loss scenarios. Strong mathematical skills are beneficial."
    },
    {
      question: "What are the prerequisites for the Derivatives exam?",
      answer: "You should complete either the RSE or ISE exam before taking the Derivatives exam, as it builds on foundational securities knowledge."
    },
    {
      question: "How long should I study for the Derivatives exam?",
      answer: "We recommend 8-10 weeks of dedicated study time. The Derivatives exam is considered the most challenging CIRO exam due to its mathematical and analytical requirements."
    },
    {
      question: "What careers require the Derivatives exam?",
      answer: "The Derivatives exam is required for Options Traders, Derivatives Analysts, Risk Managers, and Portfolio Managers who deal with derivative products."
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Derivatives Exam Prep 2025 - Options, Futures & Swaps Course | CIRO Certified"
        description="Pass the CIRO Derivatives Exam with Canada's leading prep course. 120 questions, 3 hours exam. Master options, futures, forwards, swaps & hedging strategies. Expert video lessons, practice questions, mock exams. Complete derivatives regulation Canada coverage."
        keywords="Derivatives exam, CIRO Derivatives Exam, derivatives exam syllabus, derivatives exam study guide, derivatives exam prep, options futures swaps forwards exam prep, derivatives regulation Canada, options exam, futures trading exam, derivatives certification, options strategies exam, hedging exam, derivatives mock exam, derivatives question bank, derivatives pass score, derivatives exam format, derivatives exam fees, derivatives 30 day study plan, Derivatives exam prep Canada, Derivatives exam prep Alberta, Derivatives exam prep Ontario"
        ogTitle="Derivatives Exam Prep Course - Master Options, Futures & Swaps"
        ogDescription="Complete CIRO derivatives exam preparation. Options, futures, hedging strategies, risk management. Expert instruction, practice questions, full mock exams. Industry leading pass rate."
        structuredData={derivativesStructuredData}
        faqData={derivativesFaqData}
        pageType="course"
      />

      {/* Hero Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Courses', link: '/courses' },
            { label: 'Derivatives Exam' }
          ]} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            Derivatives Exam Preparation
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            opacity: 0.95
          }}>
            Master options, futures, and derivative strategies. Comprehensive preparation for derivatives certification with expert instruction.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Enroll Now <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* About Derivatives */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>About Derivatives Certification</h2>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The <strong>Derivatives Exam</strong> is essential for investment professionals who trade or advise on options, futures, and other derivative instruments.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              This certification covers options strategies, futures contracts, hedging techniques, pricing models, and regulatory requirements for derivatives trading in Canadian markets.
            </p>
          </div>
        </div>
      </section>

      {/* Key Topics */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Derivatives Exam Coverage</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: <TrendingUp size={32} />, title: 'Options Strategies', desc: 'Calls, puts, spreads, straddles, and complex option strategies' },
              { icon: <BookOpen size={32} />, title: 'Futures Trading', desc: 'Commodity futures, index futures, and futures strategies' },
              { icon: <Shield size={32} />, title: 'Hedging Techniques', desc: 'Portfolio hedging, risk management, and protective strategies' },
              { icon: <Brain size={32} />, title: 'Pricing Models', desc: 'Black-Scholes, Greeks, and derivative valuation' },
              { icon: <Award size={32} />, title: 'Regulatory Framework', desc: 'CIRO rules and margin requirements for derivatives' },
              { icon: <Video size={32} />, title: 'Risk Management', desc: 'Derivatives risk assessment and position management' }
            ].map((topic, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>{topic.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{topic.title}</h3>
                <p className="body-small">{topic.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Pathways */}
      <VittaDerivativesPathways />

      {/* Course Features */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Derivatives Course Features</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              'Options strategy tutorials',
              'Futures trading simulations',
              'Pricing model calculators',
              'Risk management tools',
              'Practice exam questions',
              'Expert instructor support'
            ].map((feature, idx) => (
              <div key={idx} style={{
                backgroundColor: 'var(--neutral-light)',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}>
                <Award size={32} color="var(--brand-primary)" style={{ marginBottom: '12px' }} />
                <p className="body-medium" style={{ fontWeight: 600 }}>{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Materials */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>Derivatives Exam Study Materials</h2>
          <p style={{ 
            textAlign: 'center', 
            marginBottom: '48px',
            color: 'var(--text-secondary)',
            fontSize: '1.1rem'
          }}>
            Comprehensive study guides available on Amazon
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'Derivatives Interactive Quick Study Guide',
                price: '49.99',
                originalPrice: '129.99',
                features: '8 Elements + Mock Exam',
                link: '/books/derivatives-interactive',
                isInternal: true,
                desc: 'Master Derivatives with 8 elements, reading notes, games, flashcards, quizzes, and mock exam with diagnostics',
                badge: 'New! April Special'
              },
              {
                title: 'Printed Derivatives Study Guide',
                subtitle: 'Complete Study Book',
                desc: 'Comprehensive study guide for the CIRO Derivatives Exam. Covers options, futures, forwards, swaps, and all derivative instruments.',
                price: '299.99',
                link: 'https://a.co/d/0c7dhbo9',
                featured: true
              },
              {
                title: 'Derivatives Mock Exams',
                subtitle: 'Printed Mock Exams',
                desc: 'Practice with full-length mock exams that replicate the Derivatives exam in structure and difficulty.',
                link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true'
              },
              {
                title: 'Derivatives Quick Study Guide',
                subtitle: 'Short Exam Guide',
                desc: 'Top concepts guide distilling the Derivatives syllabus into the most testable, high-probability topics.',
                link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true'
              }
            ].map((product, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '12px',
                border: product.featured ? '2px solid var(--gold-primary)' : '1px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                {product.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '20px',
                    backgroundColor: 'var(--gold-primary)',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}>RECOMMENDED</div>
                )}
                <h3 className="heading-5" style={{ marginBottom: '8px' }}>{product.title}</h3>
                <p className="caption" style={{ 
                  marginBottom: '16px', 
                  color: 'var(--brand-primary)',
                  textTransform: 'none',
                  letterSpacing: 'normal'
                }}>{product.subtitle}</p>
                <p className="body-small" style={{ color: 'var(--text-secondary)', flex: 1, marginBottom: '24px' }}>{product.desc}</p>
                {product.price && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--neutral-light)',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>AMAZON PRICE</p>
                    <p style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--brand-primary)' }}>
                      CAD ${product.price}
                    </p>
                  </div>
                )}
                <a 
                  href={product.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-primary"
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  {product.price ? 'Buy on Amazon' : 'View on Amazon'}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Derivatives Course Pricing</h2>
          <div style={{
            maxWidth: '450px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: 'var(--brand-primary)',
              padding: '48px',
              borderRadius: '16px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.25)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--gold-primary)',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              }}>BEST VALUE</div>
              <h3 className="heading-4" style={{ marginBottom: '16px', color: 'white' }}>Monthly Plan</h3>
              <div style={{ fontSize: '3.5rem', fontWeight: 900, color: 'var(--gold-primary)', marginBottom: '8px' }}>$29.99</div>
              <p className="body-small" style={{ marginBottom: '32px', color: 'rgba(255,255,255,0.8)' }}>per month • Cancel anytime</p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '32px', textAlign: 'left' }}>
                {['All video lessons', 'AI Tutor access', 'Practice questions', 'Mock exams', 'Study guides'].map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <Award size={22} color="var(--gold-primary)" />
                    <span className="body-small" style={{ color: 'white', fontSize: '1rem' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout" target="_blank" rel="noopener noreferrer" className="btn-gold" style={{ width: '100%', padding: '16px 32px', fontSize: '1.1rem' }}>Get Started</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Everything you need to know about the Derivatives exam
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {derivativesFaqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--brand-primary)',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>Master Derivatives Trading</h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join thousands of successful derivatives professionals who passed with Vitta Finance Academy
          </p>
          <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" className="btn-gold">
            Enroll in Derivatives Course <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default DerivativesExam;